import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { FixedSizeGrid as VirtualGrid } from 'react-window'
import AutoSizer from 'react-virtualized-auto-sizer'
import SydModal from '../SydModal'
import SydModalActions from '../SydModal/SydModalActions'
import SydButton from '../Button'
import SydInput from '../SydInput'
import Icon from '../../atoms/Icon'
import { ICON_NAMES } from '../../../constants'

const CELL_SIZE = 80
const CELL_GAP = 8
const CELL_WITH_GAP = CELL_SIZE + CELL_GAP

const ALL_ICON_NAMES = Object.values(ICON_NAMES).filter(Boolean).sort()

const useStyles = makeStyles(() => ({
  cell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    border: '2px solid transparent',
    borderRadius: 'var(--border-radius-md)',
    backgroundColor: 'var(--color-surface-050)',
    transition: 'border-color 150ms, background-color 150ms',
    boxSizing: 'border-box',
    padding: '4px',
    '&:hover': {
      borderColor: 'var(--color-primary-300)',
      backgroundColor: 'var(--color-neutral-100)'
    }
  },
  cellSelected: {
    borderColor: 'var(--color-primary-500)',
    backgroundColor: 'var(--color-primary-050)'
  },
  cellName: {
    fontSize: '9px',
    textAlign: 'center',
    color: 'var(--text-color-subtle)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    marginTop: '2px'
  }
}))

function CellRenderer ({ columnIndex, rowIndex, style, data }) {
  const { icons, columnCount, staged, classes, onSelect } = data
  const index = rowIndex * columnCount + columnIndex
  if (index >= icons.length) return null

  const name = icons[index]
  const isSelected = staged === name

  return (
    <div style={{ ...style, padding: `${CELL_GAP / 2}px` }}>
      <div
        className={clsx(classes.cell, { [classes.cellSelected]: isSelected })}
        style={{ width: CELL_SIZE, height: CELL_SIZE }}
        onClick={() => onSelect(name)}
        title={name}
      >
        <Icon name={name} customSize='28px' />
        <div className={classes.cellName}>{name}</div>
      </div>
    </div>
  )
}

CellRenderer.propTypes = {
  columnIndex: PropTypes.number.isRequired,
  rowIndex: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired
}

function IconPickerDialog ({ open, onClose, value, onConfirm }) {
  const classes = useStyles()
  const [search, setSearch] = useState('')
  const [staged, setStaged] = useState(value || null)

  useEffect(() => {
    if (open) {
      setStaged(value || null)
      setSearch('')
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return ALL_ICON_NAMES
    const term = search.toLowerCase()
    return ALL_ICON_NAMES.filter(n => n.toLowerCase().includes(term))
  }, [search])

  const handleConfirm = useCallback(() => {
    onConfirm(staged)
    onClose()
  }, [staged, onConfirm, onClose])

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title='Select Icon'
      size='medium'
      disablePortal={false}
    >
      <div style={{ marginBottom: '12px' }}>
        <SydInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Search icons...'
          minWidth='0'
        />
      </div>

      {filtered.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '32px',
          color: 'var(--text-color-subtle)',
          fontSize: 'var(--font-size-sm)'
        }}
        >
          No icons match &ldquo;{search}&rdquo;
        </div>
      ) : (
        <div style={{ height: '360px', width: '100%' }}>
          <AutoSizer>
            {({ height, width }) => {
              const columnCount = Math.max(1, Math.floor(width / CELL_WITH_GAP))
              const rowCount = Math.ceil(filtered.length / columnCount)

              return (
                <VirtualGrid
                  columnCount={columnCount}
                  columnWidth={CELL_WITH_GAP}
                  height={height}
                  rowCount={rowCount}
                  rowHeight={CELL_WITH_GAP}
                  width={width}
                  itemData={{
                    icons: filtered,
                    columnCount,
                    staged,
                    classes,
                    onSelect: setStaged
                  }}
                >
                  {CellRenderer}
                </VirtualGrid>
              )
            }}
          </AutoSizer>
        </div>
      )}

      <SydModalActions style={{ marginTop: '12px' }}>
        <SydButton variant='outline' size='sm' onClick={onClose}>Cancel</SydButton>
        <SydButton variant='primary' size='sm' onClick={handleConfirm} disabled={!staged}>OK</SydButton>
      </SydModalActions>
    </SydModal>
  )
}

IconPickerDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  value: PropTypes.string,
  onConfirm: PropTypes.func.isRequired
}

export default IconPickerDialog
